import type {
  DataResponse,
  PaginatedResponse,
  NoticeWithMeta,
  NoticeRead,
  CreateNoticeInput,
  UpdateNoticeInput,
  ListNoticesQuery,
} from '@pms/shared';
import { api } from '@/lib/api';

export const noticeService = {
  // ── Admin ──────────────────────────────────────────────────────────────────

  async stats(): Promise<{ total: number; published: number; draft: number; archived: number }> {
    const res = await api.get<DataResponse<{ total: number; published: number; draft: number; archived: number }>>('/notices/stats');
    return res.data.data;
  },

  async list(query: Partial<ListNoticesQuery> = {}): Promise<PaginatedResponse<NoticeWithMeta>> {
    const res = await api.get<PaginatedResponse<NoticeWithMeta>>('/notices/', { params: query });
    return res.data;
  },

  async create(input: CreateNoticeInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/notices/', input);
    return res.data.data;
  },

  async update(id: string, input: UpdateNoticeInput): Promise<void> {
    await api.patch(`/notices/${id}`, input);
  },

  async publish(id: string): Promise<void> {
    await api.patch(`/notices/${id}/publish`);
  },

  async archive(id: string): Promise<void> {
    await api.patch(`/notices/${id}/archive`);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/notices/${id}`);
  },

  async listReads(
    noticeId: string,
    query: { page?: number; limit?: number } = {},
  ): Promise<PaginatedResponse<NoticeRead & { user_name: string; employee_code: string | null; email: string }>> {
    const res = await api.get(`/notices/${noticeId}/reads`, { params: query });
    return res.data;
  },

  // ── Employee ───────────────────────────────────────────────────────────────

  async myStats(): Promise<{ total: number; unread: number; acknowledged: number; pending_ack: number }> {
    const res = await api.get<DataResponse<{ total: number; unread: number; acknowledged: number; pending_ack: number }>>('/notices/my/stats');
    return res.data.data;
  },

  async listMy(query: Partial<ListNoticesQuery> = {}): Promise<PaginatedResponse<NoticeWithMeta>> {
    const res = await api.get<PaginatedResponse<NoticeWithMeta>>('/notices/my', { params: query });
    return res.data;
  },

  async get(id: string): Promise<NoticeWithMeta> {
    const res = await api.get<DataResponse<NoticeWithMeta>>(`/notices/${id}`);
    return res.data.data;
  },

  async markRead(id: string): Promise<void> {
    await api.post(`/notices/${id}/read`);
  },

  async acknowledge(id: string): Promise<void> {
    await api.post(`/notices/${id}/acknowledge`);
  },
};
